/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { WrappedCard } from './controls';
import { isCardValid, getMinOccupiedColNumber } from './utils';
import { getCardsBoardStyles } from './styles';

/**
 * Card type
 * @typedef {object} Card
 * @property {string} key - Unique key of each card used for React list node rendering.
 * @property {number} [occupiedColNumber=1] - The column number that this card occupies.
 * @property {number} [minWidth=NaN] - The minimum width of this card, it has higher priority than the defaultColMinWidth from CardsBoard.
 * @property {node} cardContainer - The card item.
 */

/**
 * Cards board with styles that can renders a list of cards in a flexible way.
 * You can decide what layout to display the cards, for example, there can be 3 columns,
 * some cards take 1 column width, some cards take 2 columns width, the others take 3 columns cards.
 * @constructor
 *
 * @param {object} props - All props.
 * @param {Array.<Card>} props.cards - Card list to display.
 * @param {string} [props.className=null] - Cards board custom class name.
 * @param {number} [props.colNumber=1] - Total column number in one row.
 * @param {number} [props.defaultColMinWidth=NaN] - Minimum width for one column. It can be overrided by the minWidth property in each card.
 * @param {bool} [props.isAutoFillBlankDisabled=false] - Whether the current-row cards will auto fill the blank spacing on the right if there is.
 */
export const CardsBoard = ({
  cards,
  className,
  colNumber,
  defaultColMinWidth,
  isAutoFillBlankDisabled,
}) => {
  const classes = useDefaultStyles(getCardsBoardStyles);

  const validCards = cards.filter(isCardValid);
  const minOccupiedColNumber = getMinOccupiedColNumber(validCards);

  const wrappedCards = validCards.map(({
    key,
    occupiedColNumber = 1,
    minWidth,
    cardContainer,
  }) => (
    <WrappedCard
      key={key}
      name={key}
      colNumber={colNumber}
      minOccupiedColNumber={minOccupiedColNumber}
      defaultColMinWidth={defaultColMinWidth}
      isAutoFillBlankDisabled={isAutoFillBlankDisabled}
      occupiedColNumber={occupiedColNumber}
      minWidth={minWidth}
    >
      {cardContainer}
    </WrappedCard>
  ));

  return (
    <div className={classNames('cards-board', classes.cardsBoard, className)}>
      {wrappedCards}
    </div>
  );
};

CardsBoard.propTypes = {
  cards: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    occupiedColNumber: PropTypes.number,
    minWidth: PropTypes.number,
    cardContainer: PropTypes.node.isRequired,
  })).isRequired,
  className: PropTypes.string,
  colNumber: PropTypes.number,
  defaultColMinWidth: PropTypes.number,
  isAutoFillBlankDisabled: PropTypes.bool,
};

CardsBoard.defaultProps = {
  className: null,
  colNumber: 1,
  // Assign NaN to avoid assign it again when passing it down to WrappedCard
  defaultColMinWidth: NaN,
  isAutoFillBlankDisabled: false,
};
